/**
 * WorldUI: in-scene hub UI for signed-in users.
 *
 * Adds a settings button to the top-left (#top-menu). Opening it shows the
 * main menu panel (user card + entry grid, styled after docs/ui/首页.jpg) and
 * its sub-panels: 世界场景 / 创建场景 / 加入场景 / 角色 / 分享链接 / 公告 /
 * 帮助 / 反馈. All data flows through the authenticated /api/v1/hub endpoints,
 * so the UI and the room management backend stay in sync (管理打通).
 *
 * Room switching hands control back to the join flow via onJoinRoom, which
 * main.ts maps to location.assign('/join?roomId=…&sceneId=…&invite=…').
 */

import type { UiManager } from './UiManager';
import { levelTitle } from '../../../../vrroom_typescript/src/platform/contracts';

type Api = <T = unknown>(path: string, method?: string, body?: unknown) => Promise<T>;

export interface WorldUIOptions {
    user: { id: number; username: string; level: number; role?: string; avatarId?: string };
    api: Api;
    /** The room this session was admitted into (used for share + "当前房间"). */
    currentRoom: { roomId: string; sceneId: string; invite: string };
    onJoinRoom: (target: { roomId: string; sceneId: string; invite: string }) => void;
    /** 世界场景（离线单人漫游）：按场景 id 直接进入，不进入联机房间。 */
    onEnterScene: (sceneId: string) => void;
    /** 角色面板切换形象成功后通知主程序：本地第三人称模型立即重建。 */
    onAvatarChange?: (avatarId: string) => void;
}

interface RoomRow {
    id: string; name: string; sceneId: string; sceneName: string;
    maxParticipants: number; hasPassword: boolean; occupiedCount: number; isHost: boolean;
    hostName?: string; createdAt?: string;
}

interface AvatarRow {
    id: string; name: string; modelUrl: string;
    config: { gender?: string; idleAnim?: string; avatarScale?: number; avatarRotationOffset?: number; groundOffset?: number; [k: string]: unknown };
}

interface AnnounceRow {
    id: number; content: string; authorName?: string; createdAt?: string;
}

interface FeedbackRow {
    id: number; username: string; content: string; createdAt?: string;
}

interface FeedbackPage {
    items: FeedbackRow[]; total: number; page: number; pageSize: number;
}

type Gender = 'all' | 'male' | 'female';

const MENU_ENTRIES: { action: string; icon: string; label: string }[] = [
    { action: 'world', icon: '🌍', label: '世界场景' },
    { action: 'create', icon: '🏗️', label: '创建场景' },
    { action: 'join', icon: '🚪', label: '加入场景' },
    { action: 'avatar', icon: '🧑', label: '角色' },
    { action: 'share', icon: '🔗', label: '分享链接' },
    { action: 'announce', icon: '📢', label: '公告' },
    { action: 'help', icon: '❓', label: '帮助' },
    { action: 'feedback', icon: '💬', label: '反馈' }
];

function h<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    node.className = className;
    if (text) node.textContent = text;
    return node;
}

async function copyText(text: string): Promise<void> {
    // clipboard API 只在安全上下文存在，且在页面失焦/权限不足时会 reject；
    // 两种失败都要退回 execCommand，否则复制按钮会静默失效。
    try {
        if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return; }
    } catch { /* fall through to execCommand */ }
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    area.style.top = '0';
    document.body.appendChild(area);
    area.focus();
    area.select();
    try { if (!document.execCommand('copy')) throw new Error('复制失败'); }
    finally { area.remove(); }
}

const errorMessage = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** 场景缩略图：优先加载 /assets/scenes/<id>/thumb.png，失败回退首字母渐变块。 */
function sceneThumb(sceneId: string, name: string): HTMLElement {
    const thumb = document.createElement('div');
    thumb.className = 'ws-thumb';
    thumb.classList.add(`ws-thumb-${Math.abs(hashCode(sceneId)) % 5}`);
    const fallback = document.createElement('span');
    fallback.textContent = name.slice(0, 1);
    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    img.addEventListener('error', () => img.remove());
    img.addEventListener('load', () => fallback.remove());
    img.src = `/assets/scenes/${encodeURIComponent(sceneId)}/thumb.png`;
    thumb.append(fallback, img);
    return thumb;
}

function hashCode(text: string): number {
    let v = 0;
    for (let i = 0; i < text.length; i++) v = (v * 31 + text.charCodeAt(i)) | 0;
    return v;
}

/** 房间卡片创建时间：MM-DD HH:mm（卡片内空间有限，省略年份）。 */
function formatRoomTime(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 反馈/公告完整时间：YYYY-MM-DD HH:mm。 */
function formatDateTime(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

class WorldUI {
    private ui: UiManager;
    private opts: WorldUIOptions;
    private menu: HTMLElement;
    private menuBtn: HTMLElement;
    private fullscreen: HTMLElement | null = null;
    private fullscreenCleanups: (() => void)[] = [];
    private modal: HTMLElement | null = null;

    constructor(ui: UiManager, opts: WorldUIOptions) {
        this.ui = ui;
        this.opts = opts;
        this.menuBtn = this._buildSettingsButton();
        this.menu = this._buildMenu();
        window.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            if (this.modal) { this.closeModal(); return; }
            if (this.fullscreen) { this.closeFullscreen(); return; }
            if (!this.menu.classList.contains('hidden')) this.closeMenu();
        });
    }

    // ── Settings button + main menu ────────────────────────────────────────

    private _buildSettingsButton(): HTMLElement {
        // 初始隐藏：场景就绪后由 revealSettingsButton() 显示。
        const btn = h('button', 'hidden');
        btn.id = 'world-settings-btn';
        btn.ariaLabel = '设置';
        btn.title = '设置';
        // 齿轮图标（lucide settings path），随按钮 currentColor 取墨玉鎏金色
        btn.innerHTML = '<svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
            + '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>'
            + '<circle cx="12" cy="12" r="3"/></svg>';
        btn.addEventListener('click', () => this.toggleMenu());
        const menu = document.getElementById('top-menu');
        (menu ?? document.body).prepend(btn);
        return btn;
    }

    /** 场景就绪后显示 ⚙ 设置按钮（登录/场景加载阶段保持隐藏）。 */
    revealSettingsButton(): void {
        this.menuBtn.classList.remove('hidden');
    }

    private _buildMenu(): HTMLElement {
        const panel = h('div', 'hidden');
        panel.id = 'world-menu';
        const u = this.opts.user;
        const head = h('div', 'wm-head');
        const avatar = h('div', 'wm-avatar', u.username.slice(0, 1).toUpperCase());
        const id = h('div', 'wm-id');
        id.append(h('strong', '', u.username), h('span', '', `ID ${u.id} · ${levelTitle(u.level)} ${u.level}级`));
        head.append(avatar, id);
        const tagline = h('div', 'wm-tagline', '游戏化改造世界');
        const grid = h('div', 'wm-grid');
        for (const entry of MENU_ENTRIES) {
            const btn = h('button', 'wm-item');
            btn.append(h('span', 'wm-icon', entry.icon), h('span', 'wm-label', entry.label));
            btn.addEventListener('click', () => { this.closeMenu(); this._dispatch(entry.action); });
            grid.append(btn);
        }
        panel.append(head, tagline, grid);
        panel.addEventListener('click', (e) => { if (e.target === panel) this.closeMenu(); });
        const layer = document.getElementById('ui-layer') ?? document.body;
        layer.appendChild(panel);
        return panel;
    }

    private _dispatch(action: string): void {
        switch (action) {
            case 'world': void this.openWorld(); break;
            case 'create': void this.openCreate(); break;
            case 'join': void this.openJoin(); break;
            case 'avatar': void this.openAvatar(); break;
            case 'share': void this.openShare(); break;
            case 'announce': this.openAnnounce(); break;
            case 'help': this.ui.toggleHelp(true); break;
            case 'feedback': this.openFeedback(); break;
        }
    }

    toggleMenu(): void {
        const opening = this.menu.classList.contains('hidden');
        if (opening) {
            this.closeFullscreen();
            this.closeModal();
            this.menu.classList.remove('hidden');
            this.ui.requestUnlock?.();
            this.ui.onPopupOpen?.();
        } else {
            this.closeMenu();
        }
    }

    closeMenu(): void {
        this.menu.classList.add('hidden');
        this.ui.onPopupClose?.();
    }

    /** 保存资料后同步主菜单里的头像字与用户名。 */
    private refreshMenuIdentity(): void {
        const u = this.opts.user;
        const strong = this.menu.querySelector('.wm-id strong');
        if (strong instanceof HTMLElement) strong.textContent = u.username;
        const avatar = this.menu.querySelector('.wm-avatar');
        if (avatar instanceof HTMLElement) avatar.textContent = u.username.slice(0, 1).toUpperCase();
    }

    // ── Shared chrome: fullscreen panel + small modal ──────────────────────

    /** Fullscreen panel (世界场景/创建场景/加入场景/角色). build() fills the
     *  body; optional register(fn) collects cleanup (3D app destroy, timers). */
    private _openFullscreen(title: string, icon: string, build: (body: HTMLElement, register: (fn: () => void) => void) => void): void {
        this.closeFullscreen();
        this.closeModal();
        this.closeMenu();
        const panel = h('div');
        panel.id = 'world-fullscreen';
        const box = h('div', 'wf-box');
        const header = h('div', 'wf-header');
        header.append(h('span', 'wf-icon', icon), h('span', 'wf-title', title));
        const close = h('button', 'wf-close', '×');
        header.append(close);
        const body = h('div', 'wf-body');
        box.append(header, body);
        panel.append(box);
        const layer = document.getElementById('ui-layer') ?? document.body;
        layer.appendChild(panel);
        close.addEventListener('click', () => this.closeFullscreen());
        panel.addEventListener('click', (e) => { if (e.target === panel) this.closeFullscreen(); });
        this.fullscreen = panel;
        this.fullscreenCleanups = [];
        build(body, (fn) => this.fullscreenCleanups.push(fn));
        this.ui.requestUnlock?.();
        this.ui.onPopupOpen?.();
    }

    closeFullscreen(): void {
        if (!this.fullscreen) return;
        const cleanups = this.fullscreenCleanups;
        this.fullscreenCleanups = [];
        cleanups.splice(0).forEach((fn) => { try { fn(); } catch { /* ignore */ } });
        this.fullscreen.remove();
        this.fullscreen = null;
        this.ui.onPopupClose?.();
    }

    /** Small centered modal (分享/公告/反馈). */
    private _openModal(title: string): HTMLElement {
        this.closeModal();
        this.closeMenu();
        const panel = h('div');
        panel.id = 'world-modal';
        const card = h('div', 'wm-modal-card');
        const close = h('button', 'wm-modal-close', '×');
        card.append(close, h('h3', '', title));
        const body = h('div', 'wm-modal-body');
        card.append(body);
        panel.append(card);
        const layer = document.getElementById('ui-layer') ?? document.body;
        layer.appendChild(panel);
        close.addEventListener('click', () => this.closeModal());
        panel.addEventListener('click', (e) => { if (e.target === panel) this.closeModal(); });
        this.modal = panel;
        this.ui.requestUnlock?.();
        this.ui.onPopupOpen?.();
        return body;
    }

    closeModal(): void {
        if (!this.modal) return;
        this.modal.remove();
        this.modal = null;
        this.ui.onPopupClose?.();
    }

    // ── Data helpers ───────────────────────────────────────────────────────

    private async _rooms(): Promise<RoomRow[]> {
        return this.opts.api<RoomRow[]>('/hub/rooms');
    }

    private async _joinRoom(roomId: string): Promise<void> {
        // 进房前预检人数：满员直接提示，不整页跳转（服务端 room:join 仍是最终裁决）。
        // 房主/管理员与创建者豁免满员（服务端行为一致），预检同样放行。
        try {
            const target = (await this._rooms()).find((r) => r.id === roomId);
            if (target && !target.isHost && target.occupiedCount >= target.maxParticipants) {
                this.ui.showHint('房间人数已满', 5000);
                return;
            }
        } catch { /* 列表拉取失败不阻塞，交给服务端校验 */ }
        try {
            const t = await this.opts.api<{ roomId: string; sceneId: string; invite: string }>(
                `/hub/rooms/${encodeURIComponent(roomId)}/invite`, 'POST');
            this.opts.onJoinRoom(t);
        } catch (err) {
            this.ui.showHint(`进入房间失败：${errorMessage(err)}`, 5000);
        }
    }

    /** Simple footer pager: ‹ prev / page info / next ›. */
    private _pager(footer: HTMLElement, perPage: number, show: (page: number) => void): { setTotal(total: number): void } {
        let page = 1;
        let pages = 1;
        const prev = h('button', 'wf-pager-btn', '‹');
        const next = h('button', 'wf-pager-btn', '›');
        const info = h('span', 'wf-pager-info');
        const sync = () => {
            info.textContent = `${page} / ${pages}`;
            prev.disabled = page <= 1;
            next.disabled = page >= pages;
        };
        prev.addEventListener('click', () => { if (page > 1) { page--; sync(); show(page); } });
        next.addEventListener('click', () => { if (page < pages) { page++; sync(); show(page); } });
        footer.append(prev, info, next);
        return {
            setTotal(total: number) {
                pages = Math.max(1, Math.ceil(total / perPage));
                if (page > pages) page = pages;
                sync();
                show(page);
            }
        };
    }

    private _pageOf<T>(items: T[], page: number, perPage: number): T[] {
        return items.slice((page - 1) * perPage, page * perPage);
    }

    // ── 世界场景（离线单人漫游：只列场景，无房间/人数等联机数据）────────────

    private async openWorld(): Promise<void> {
        let scenes: { id: string; name: string }[];
        try { scenes = await this.opts.api<{ id: string; name: string }[]>('/hub/scenes'); }
        catch (err) { this.ui.showHint(`场景列表加载失败：${errorMessage(err)}`); return; }
        this._openFullscreen('世界场景', '🌍', (body) => {
            body.append(h('p', 'ws-solo-note',
                '离线单人漫游：不进房间 · 不连服务器 · 不同步其他玩家，场景中只有你一人。点击卡片选中场景，再点下方「进入」开始漫游。'));
            const grid = h('div', 'ws-grid ws-grid-scene');
            const footer = h('div', 'wf-footer');
            let pickedId: string | null = null;
            let page = 1;
            const enter = h('button', 'wc-confirm', '进入');
            enter.disabled = true;
            // 原地切换选中态，不重建网格，避免缩略图重新加载闪烁。
            const syncPicked = () => {
                grid.querySelectorAll('.ws-card').forEach((n) =>
                    n.classList.toggle('picked', (n as HTMLElement).dataset.sceneId === pickedId));
            };
            const pick = (id: string) => { pickedId = id; enter.disabled = false; syncPicked(); };
            const render = () => {
                grid.replaceChildren(...this._pageOf(scenes, page, 8).map((scene) => this._sceneCard(scene, scene.id === pickedId, pick)));
                if (!scenes.length) grid.append(h('p', 'wf-empty', '暂无可用场景，敬请期待。'));
            };
            const pager = this._pager(footer, 8, (p) => { page = p; render(); });
            enter.addEventListener('click', () => { if (pickedId) this.opts.onEnterScene(pickedId); });
            // 「进入」按钮独立成行，放在网格下方水平居中；footer 只留分页器。
            const enterRow = h('div', 'ws-enter-row');
            enterRow.append(enter);
            body.append(grid, footer, enterRow);
            pager.setTotal(scenes.length);
        });
    }

    /** 场景卡片：缩略图 + 场景名；点击仅选中，「进入」按钮才真正进入。 */
    private _sceneCard(scene: { id: string; name: string }, picked: boolean, onPick: (id: string) => void): HTMLElement {
        const card = h('button', 'ws-card');
        if (picked) card.classList.add('picked');
        card.title = '点击选中';
        card.dataset.sceneId = scene.id;
        card.append(sceneThumb(scene.id, scene.name), h('div', 'ws-name', scene.name));
        card.addEventListener('click', () => onPick(scene.id));
        return card;
    }

    // ── 加入场景（联机房间）──────────────────────────────────────────────────

    private async openJoin(): Promise<void> {
        let rooms: RoomRow[];
        try { rooms = await this._rooms(); }
        catch (err) { this.ui.showHint(`房间列表加载失败：${errorMessage(err)}`); return; }
        this._openFullscreen('加入场景', '🚪', (body) => {
            const grid = h('div', 'ws-grid');
            const footer = h('div', 'wf-footer');
            let pickedId: string | null = null;
            let page = 1;
            const enter = h('button', 'wc-confirm', '进入');
            enter.disabled = true;
            const refresh = h('button', 'wj-refresh', '⟳ 刷新');
            refresh.addEventListener('click', () => { this.closeFullscreen(); void this.openJoin(); });
            const pick = (id: string) => {
                pickedId = id;
                enter.disabled = false;
                // 原地切换选中态，不重建网格，避免缩略图重新加载闪烁。
                grid.querySelectorAll('.ws-card').forEach((n) =>
                    n.classList.toggle('picked', (n as HTMLElement).dataset.roomId === pickedId));
            };
            const render = () => {
                grid.replaceChildren(...this._pageOf(rooms, page, 8).map((room) => this._worldCard(room, room.id === pickedId, pick)));
                if (!rooms.length) grid.append(h('p', 'wf-empty', '暂无开放房间，可以自己创建一个。'));
            };
            // 刷新按钮放在分页器（‹）左侧，进入按钮在最后。
            footer.append(refresh);
            const pager = this._pager(footer, 8, (p) => { page = p; render(); });
            enter.addEventListener('click', () => { if (pickedId) void this._joinRoom(pickedId); });
            footer.append(enter);
            body.append(grid, footer);
            pager.setTotal(rooms.length);
        });
    }

    /** 房间卡片：与「世界场景」同款 ws-card，人数后附创建人；创建时间另起一行
     *  左对齐。点击仅选中，「进入」按钮才真正进房。 */
    private _worldCard(room: RoomRow, picked: boolean, onPick: (id: string) => void): HTMLElement {
        const card = h('button', 'ws-card');
        if (picked) card.classList.add('picked');
        card.title = '点击选中';
        card.dataset.roomId = room.id;
        const thumb = sceneThumb(room.sceneId, room.sceneName);
        const name = h('div', 'ws-name', room.name);
        const scene = h('div', 'ws-scene', room.sceneName);
        const meta = h('div', 'ws-meta');
        meta.append(h('span', 'ws-tag', room.hasPassword ? '加密' : '公共'));
        meta.append(h('span', 'ws-people', `${room.occupiedCount}/${room.maxParticipants} 人`));
        if (room.hostName) meta.append(h('span', 'ws-host', room.hostName));
        if (room.id === this.opts.currentRoom.roomId) meta.append(h('span', 'wj-current', '当前房间'));
        card.append(thumb, name, scene, meta);
        const createdText = formatRoomTime(room.createdAt);
        if (createdText) card.append(h('div', 'ws-time-line', `创建于 ${createdText}`));
        card.addEventListener('click', () => onPick(room.id));
        return card;
    }

    // ── 创建场景 ────────────────────────────────────────────────────────────

    private async openCreate(): Promise<void> {
        let scenes: { id: string; name: string }[];
        try { scenes = await this.opts.api<{ id: string; name: string }[]>('/hub/scenes'); }
        catch (err) { this.ui.showHint(`场景模板加载失败：${errorMessage(err)}`); return; }
        if (!scenes.length) { this.ui.showHint('暂无可用场景模板'); return; }
        this._openFullscreen('创建场景', '🏗️', (body) => {
            let sceneId = scenes[0].id;
            const PER_PAGE = 6; // 每页 2 排 × 3 列
            let page = 0;
            const pageCount = Math.max(1, Math.ceil(scenes.length / PER_PAGE));
            const grid = h('div', 'wc-grid');
            const cards = scenes.map((s) => {
                const card = h('button', 'wc-card');
                const thumb = sceneThumb(s.id, s.name);
                card.append(thumb, h('div', 'ws-name', s.name));
                const pick = () => {
                    sceneId = s.id;
                    grid.querySelectorAll('.wc-card').forEach((n) => n.classList.remove('picked'));
                    card.classList.add('picked');
                    if (!name.value.trim() || scenes.some((x) => x.name === name.value.trim())) name.value = s.name;
                };
                card.addEventListener('click', pick);
                return { card, pick };
            });
            cards[0].card.classList.add('picked');
            const prev = h('button', 'wc-page-btn wc-page-prev', '‹');
            const next = h('button', 'wc-page-btn wc-page-next', '›');
            const indicator = h('span', 'wc-page-ind');
            const renderPage = () => {
                grid.innerHTML = '';
                for (const c of cards.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)) grid.append(c.card);
                indicator.textContent = `${page + 1} / ${pageCount}`;
            };
            prev.addEventListener('click', () => { page = (page - 1 + pageCount) % pageCount; renderPage(); });
            next.addEventListener('click', () => { page = (page + 1) % pageCount; renderPage(); });
            renderPage();
            const carousel = h('div', 'wc-carousel');
            carousel.append(grid, prev, next, indicator);
            const form = h('div', 'wc-form');
            const nameRow = h('label', 'wc-field');
            nameRow.append(h('span', '', '房间名称'));
            const name = h('input');
            name.type = 'text';
            name.maxLength = 100;
            name.value = scenes[0].name;
            nameRow.append(name);
            const maxRow = h('label', 'wc-field');
            maxRow.append(h('span', '', '最大人数'));
            const maxWrap = h('div', 'wc-max-wrap');
            const max = h('input');
            max.type = 'range';
            max.min = '2';
            max.max = '200';
            max.step = '1';
            max.value = '20';
            const maxVal = h('span', 'wc-max-val', '20');
            max.addEventListener('input', () => { maxVal.textContent = max.value; });
            maxWrap.append(max, maxVal);
            maxRow.append(maxWrap);
            const passRow = h('label', 'wc-field');
            passRow.append(h('span', '', '房间密码（可选）'));
            const pass = h('input');
            pass.type = 'password';
            pass.maxLength = 64;
            pass.autocomplete = 'new-password';
            pass.placeholder = '留空则无需密码';
            passRow.append(pass);
            const msg = h('p', 'wc-msg');
            const formRow = h('div', 'wc-form-row');
            formRow.append(nameRow, maxRow, passRow);
            form.append(formRow, msg);
            const actions = h('div', 'wf-footer wc-actions');
            const confirm = h('button', 'wc-confirm', '确认');
            confirm.addEventListener('click', async () => {
                const roomName = name.value.trim();
                if (!roomName) { msg.textContent = '请填写房间名称'; return; }
                confirm.disabled = true;
                msg.textContent = '正在创建…';
                try {
                    const payload: Record<string, unknown> = {
                        name: roomName,
                        sceneId,
                        maxParticipants: Number(max.value)
                    };
                    if (pass.value) payload.password = pass.value;
                    const created = await this.opts.api<{ roomId: string; sceneId: string; invite: string }>('/hub/rooms', 'POST', payload);
                    this.opts.onJoinRoom(created);
                } catch (err) {
                    msg.textContent = errorMessage(err);
                    confirm.disabled = false;
                }
            });
            actions.append(confirm);
            body.append(carousel, form, actions);
        });
    }

    // ── 分享链接 ────────────────────────────────────────────────────────────

    private async openShare(): Promise<void> {
        const body = this._openModal('邀请加入');
        const { roomId, sceneId, invite } = this.opts.currentRoom;
        let roomName = '';
        try { roomName = (await this._rooms()).find((r) => r.id === roomId)?.name ?? ''; } catch { /* name optional */ }
        body.append(h('p', 'wshare-sub', '邀请您加入南农元宇宙'));
        body.append(h('p', 'wshare-room', `房间主题：${roomName || roomId}`));
        body.append(h('p', 'wshare-label', '点击链接，加入场景：'));
        const full = `${location.origin}/join?${new URLSearchParams({ roomId, sceneId, invite })}`;
        const guest = `${location.origin}/join?${new URLSearchParams({ roomId, sceneId })}`;
        const url = h('div', 'wshare-url', full);
        body.append(url);
        const actions = h('div', 'wshare-actions');
        // 复制内容带上邀请文案与房间主题，方便直接粘贴到聊天工具。
        const shareText = (link: string) =>
            `邀请您加入南农元宇宙\n房间主题：${roomName || roomId}\n\n点击链接，加入场景：\n${link}`;
        const mkCopy = (label: string, text: string) => {
            const btn = h('button', 'wshare-btn', label);
            btn.addEventListener('click', async () => {
                try { await copyText(text); this.ui.showHint('链接已复制'); }
                catch { this.ui.showHint('复制失败，请手动选择链接复制'); }
            });
            return btn;
        };
        actions.append(mkCopy('复制邀请链接', shareText(full)), mkCopy('复制游客链接', shareText(guest)));
        body.append(actions);
    }

    // ── 公告 / 反馈 ────────────────────────────────────────────────────────

    /** 公告弹窗：所有人看公告列表；管理员额外在下方获得「输入框 + 发布按钮」，
     *  发布成功后原地刷新列表。风格沿用反馈表单（玻璃输入框 + wshare-btn）。 */
    private async openAnnounce(): Promise<void> {
        const body = this._openModal('公告');
        const isAdmin = this.opts.user.role === 'admin';
        const list = h('ul', 'wannounce');
        const box = h('div', 'wannounce-box');
        box.append(list);
        body.append(box);
        let area: HTMLTextAreaElement | null = null;
        let msg: HTMLElement | null = null;
        if (isAdmin) {
            area = h('textarea', 'wfeedback-area');
            area.rows = 4;
            area.placeholder = '输入要发布的公告内容…';
            const actions = h('div', 'wshare-actions');
            const publish = h('button', 'wshare-btn', '发布公告');
            msg = h('p', 'wc-msg');
            publish.addEventListener('click', async () => {
                const content = (area?.value ?? '').trim();
                if (!content) { msg!.textContent = '请输入公告内容。'; return; }
                publish.disabled = true;
                msg!.textContent = '正在发布…';
                try {
                    await this.opts.api('/admin/announcements', 'POST', { content });
                    area!.value = '';
                    msg!.textContent = '发布成功。';
                    this.ui.showHint('公告已发布');
                    await load();
                } catch (err) {
                    msg!.textContent = errorMessage(err);
                } finally {
                    publish.disabled = false;
                }
            });
            actions.append(publish);
            body.append(area, actions, msg);
        }
        const load = async (): Promise<void> => {
            try {
                const rows = await this.opts.api<AnnounceRow[]>('/hub/announcements');
                list.replaceChildren(...rows.map((r) => h('li', '', r.content)));
                if (!rows.length) list.append(h('li', '', '暂无公告。'));
            } catch (err) {
                list.replaceChildren(h('li', '', `公告加载失败：${errorMessage(err)}`));
            }
        };
        await load();
    }

    /** 反馈弹窗按角色分流：
     *  - 普通用户：表单提交反馈（POST /hub/feedbacks）；
     *  - 管理员：分页浏览所有用户反馈（反馈人 / 问题描述 / 提交时间），
     *    一页 6 条，列表在玻璃盒内滚动（滚动条与帮助页同款），底部 ‹ › 翻页。 */
    private async openFeedback(): Promise<void> {
        if (this.opts.user.role === 'admin') { await this._openFeedbackAdmin(); return; }
        const body = this._openModal('反馈');
        const tips = h('p', 'wf-empty', '遇到问题或有建议？写在下面告诉我们，管理员会在后台查看。');
        const area = h('textarea', 'wfeedback-area');
        area.rows = 5;
        area.placeholder = '请描述你遇到的问题或建议…';
        const actions = h('div', 'wshare-actions');
        const msg = h('p', 'wc-msg');
        const submit = h('button', 'wshare-btn', '提交反馈');
        submit.addEventListener('click', async () => {
            const content = area.value.trim();
            if (!content) { msg.textContent = '请输入反馈内容。'; return; }
            submit.disabled = true;
            msg.textContent = '正在提交…';
            try {
                await this.opts.api('/hub/feedbacks', 'POST', { content });
                area.value = '';
                msg.textContent = '已收到你的反馈，感谢！';
            } catch (err) {
                msg.textContent = errorMessage(err);
            } finally {
                submit.disabled = false;
            }
        });
        actions.append(submit);
        body.append(tips, area, actions, msg);
    }

    /** 管理员反馈列表：服务端分页（6 条/页），滚动容器与翻页器。
     *  不复用 _pager（其 setTotal 会回调 show 形成递归），这里独立维护页码。 */
    private async _openFeedbackAdmin(): Promise<void> {
        const body = this._openModal('反馈');
        const list = h('div', 'wannounce-box wfb-list');
        const footer = h('div', 'wf-footer');
        const prev = h('button', 'wf-pager-btn', '‹');
        const next = h('button', 'wf-pager-btn', '›');
        const info = h('span', 'wf-pager-info');
        let page = 1;
        let pages = 1;
        const sync = () => {
            info.textContent = `${page} / ${pages}`;
            prev.disabled = page <= 1;
            next.disabled = page >= pages;
        };
        prev.addEventListener('click', () => { if (page > 1) { page--; void render(); } });
        next.addEventListener('click', () => { if (page < pages) { page++; void render(); } });
        footer.append(prev, info, next);
        const render = async (): Promise<void> => {
            try {
                const data = await this.opts.api<FeedbackPage>(
                    `/admin/feedbacks?page=${page}&pageSize=6`);
                list.replaceChildren(...data.items.map((row) => {
                    const item = h('div', 'wfb-item');
                    const head = h('div', 'wfb-head');
                    head.append(
                        h('span', 'wfb-name', row.username),
                        h('span', 'wfb-time', formatDateTime(row.createdAt)));
                    item.append(head, h('p', 'wfb-content', row.content));
                    return item;
                }));
                if (!data.items.length)
                    list.replaceChildren(h('p', 'wf-empty', '暂无用户反馈。'));
                pages = Math.max(1, Math.ceil(data.total / data.pageSize));
                if (page > pages) { page = pages; void render(); return; }
            } catch (err) {
                list.replaceChildren(h('p', 'wf-empty', `反馈加载失败：${errorMessage(err)}`));
            }
            sync();
        };
        body.append(list, footer);
        sync();
        await render();
    }

    // ── 角色（个人中心）────────────────────────────────────────────────────

    private async openAvatar(): Promise<void> {
        let avatars: AvatarRow[];
        try { avatars = await this.opts.api<AvatarRow[]>('/hub/avatars'); }
        catch (err) { this.ui.showHint(`形象列表加载失败：${errorMessage(err)}`); return; }
        this._openFullscreen('角色', '🧑', (body, register) => {
            const tabs = h('div', 'wf-tabs');
            const content = h('div', 'wa-content');
            body.append(tabs, content);
            const defs: { id: string; label: string }[] = [
                { id: 'profile', label: '基础信息' },
                { id: 'model', label: '角色模型' },
                { id: 'security', label: '账号安全' }
            ];
            let active = 'profile';
            let stage: AvatarStage | null = null;
            const tabBtns = new Map<string, HTMLElement>();
            for (const d of defs) {
                const btn = h('button', 'wf-tab', d.label);
                btn.addEventListener('click', () => { active = d.id; syncTabs(); render(); });
                tabs.append(btn);
                tabBtns.set(d.id, btn);
            }
            const syncTabs = () => {
                for (const [id, btn] of tabBtns) btn.classList.toggle('active', id === active);
            };
            const render = () => {
                stage?.destroy();
                stage = null;
                content.replaceChildren();
                if (active === 'profile') this._renderProfileTab(content);
                else if (active === 'security') this._renderSecurityTab(content);
                else this._renderModelTab(content, avatars, (s) => { stage = s; });
            };
            syncTabs();
            render();
            register(() => { stage?.destroy(); stage = null; });
        });
    }

    /** 页签一：基础信息（用户名 / 单位 → PATCH /me）。 */
    private _renderProfileTab(content: HTMLElement): void {
        const u = this.opts.user;
        const card = h('div', 'wa-card');
        card.append(h('p', 'wa-card-note', `用户编号 ${u.id} · ${levelTitle(u.level)} ${u.level}级`));
        const mk = (label: string, value: string, min: number, max: number) => {
            const row = h('label', 'wc-field');
            row.append(h('span', '', label));
            const input = h('input');
            input.type = 'text';
            input.value = value;
            input.minLength = min;
            input.maxLength = max;
            row.append(input);
            card.append(row);
            return input;
        };
        const name = mk('用户名（公开昵称）', u.username, 2, 20);
        const org = mk('单位', (u as unknown as { organization?: string }).organization ?? '', 2, 40);
        // 性别选择：两个互斥按钮，不选则默认男。
        let gender: 'male' | 'female' =
            (u as unknown as { gender?: string }).gender === 'female' ? 'female' : 'male';
        const genderField = h('div', 'wc-field');
        genderField.append(h('span', '', '性别'));
        const genderRow = h('div', 'wc-gender');
        const genderBtns = ([['male', '男'], ['female', '女']] as const).map(([key, label]) => {
            const b = h('button', 'wc-gender-btn', label);
            b.type = 'button';
            b.addEventListener('click', () => { gender = key; syncGender(); });
            genderRow.append(b);
            return { key, b };
        });
        const syncGender = () => genderBtns.forEach(({ key, b }) => b.classList.toggle('active', gender === key));
        syncGender();
        genderField.append(genderRow);
        name.closest('.wc-field')!.after(genderField);
        // 隐身模式：勾选后同房间其他玩家看不到自己（模型 + 名牌均隐藏）。
        const hiddenField = h('label', 'wc-field wc-check-field');
        const hiddenText = h('span', '', '隐身模式（别人看不到我）');
        const hidden = h('input');
        hidden.type = 'checkbox';
        hidden.checked = (u as unknown as { hidden?: boolean }).hidden === true;
        const hiddenWrap = h('div', 'wc-check');
        hiddenWrap.append(hidden, h('span', '', '开启后，房间内其他玩家将看不到我的角色与昵称'));
        hiddenField.append(hiddenText, hiddenWrap);
        org.closest('.wc-field')!.after(hiddenField);
        const msg = h('p', 'wc-msg');
        const save = h('button', 'wc-confirm', '保存资料');
        save.addEventListener('click', async () => {
            save.disabled = true;
            msg.textContent = '正在保存…';
            try {
                const updated = await this.opts.api<Record<string, unknown>>('/me', 'PATCH', {
                    username: name.value.trim(),
                    organization: org.value.trim(),
                    gender,
                    hidden: hidden.checked
                });
                Object.assign(this.opts.user, updated);
                this.refreshMenuIdentity();
                msg.textContent = '已保存。';
                this.ui.showHint('资料已更新');
            } catch (err) {
                msg.textContent = errorMessage(err);
            } finally {
                save.disabled = false;
            }
        });
        card.append(msg, save);
        content.append(card);
    }

    /** 页签三：账号安全（修改密码 → POST /me/password → 重新登录）。 */
    private _renderSecurityTab(content: HTMLElement): void {
        const card = h('div', 'wa-card');
        card.append(h('p', 'wa-card-note', '修改成功后需要重新登录。'));
        const mk = (label: string) => {
            const row = h('label', 'wc-field');
            row.append(h('span', '', label));
            const input = h('input');
            input.type = 'password';
            input.maxLength = 64;
            input.autocomplete = 'new-password';
            row.append(input);
            card.append(row);
            return input;
        };
        const oldPass = mk('当前密码');
        const newPass = mk('新密码（8–64 位）');
        newPass.autocomplete = 'new-password';
        const confirm = mk('确认新密码');
        const msg = h('p', 'wc-msg');
        const save = h('button', 'wc-confirm', '更新密码');
        save.addEventListener('click', async () => {
            if (newPass.value !== confirm.value) { msg.textContent = '两次输入的新密码不一致。'; return; }
            save.disabled = true;
            msg.textContent = '正在更新…';
            try {
                await this.opts.api('/me/password', 'POST', { oldPassword: oldPass.value, password: newPass.value });
                msg.textContent = '密码已更新，正在返回登录…';
                this.ui.showHint('密码已更新');
                window.setTimeout(() => location.assign('/'), 1200);
            } catch (err) {
                msg.textContent = errorMessage(err);
                save.disabled = false;
            }
        });
        card.append(msg, save);
        content.append(card);
    }

    /** 页签二：角色模型（中心 3D 展示 + 左右切换 + 拖拽/缩放）。
     *  性别已在「基础信息」中选定：本页签锁定为对应性别，仅展示该性别的形象，不可切换。
     *  setStage 把异步创建完成的 3D 舞台交还给页签容器统一销毁。 */
    private _renderModelTab(content: HTMLElement, avatars: AvatarRow[], setStage: (stage: AvatarStage) => void): void {
        if (!avatars.length) {
            content.append(h('p', 'wf-empty', '暂无可用的角色形象，请联系管理员上传。'));
            return;
        }
        const gender: Gender =
            (this.opts.user as unknown as { gender?: string }).gender === 'female' ? 'female' : 'male';
        let idx = 0;
        let stage: AvatarStage | null = null;
        const wrap = h('div', 'wa-stage');
        const canvasBox = h('div', 'wa-canvas-box');
        const note = h('div', 'wa-note', '正在加载…');
        const left = h('button', 'wa-arrow wa-arrow-left', '‹');
        const right = h('button', 'wa-arrow wa-arrow-right', '›');
        const hud = h('div', 'wa-hud');
        const name = h('div', 'wa-name');
        const use = h('button', 'wa-use', '使用此形象');
        const hint = h('div', 'wa-hint', '拖拽旋转 · 滚轮 / 双指缩放');
        hud.append(name, use, hint);
        wrap.append(canvasBox, note, left, right, hud);
        content.append(wrap);

        const filtered = (): AvatarRow[] =>
            avatars.filter((a) => (a.config?.gender ?? 'neutral') === gender);
        const syncHud = () => {
            const list = filtered();
            const cur = list[idx];
            name.textContent = cur ? `${cur.name}${cur.id === this.opts.user.avatarId ? ' · 使用中' : ''}` : '—';
            use.disabled = !cur || cur.id === this.opts.user.avatarId;
            left.disabled = list.length <= 1;
            right.disabled = list.length <= 1;
        };
        const show = async (): Promise<void> => {
            const list = filtered();
            if (!list.length) {
                name.textContent = '—';
                note.textContent = '该分类下暂无形象';
                note.classList.remove('hidden');
                return;
            }
            syncHud();
            note.textContent = '正在加载模型…';
            note.classList.remove('hidden');
            if (!stage) {
                stage = await AvatarStage.create(canvasBox);
                if (!canvasBox.isConnected) { stage.destroy(); stage = null; return; }
                setStage(stage);
            }
            await stage.show(list[idx]);
            note.classList.add('hidden');
        };
        left.addEventListener('click', () => {
            const list = filtered();
            idx = (idx - 1 + list.length) % list.length;
            void show();
        });
        right.addEventListener('click', () => {
            const list = filtered();
            idx = (idx + 1) % list.length;
            void show();
        });
        use.addEventListener('click', async () => {
            const cur = filtered()[idx];
            if (!cur || use.disabled) return;
            use.disabled = true;
            try {
                const updated = await this.opts.api<Record<string, unknown>>('/me', 'PATCH', { avatarId: cur.id });
                Object.assign(this.opts.user, updated);
                this.ui.showHint('形象已切换');
                // 本地第三人称模型立即重建；同房间的其他人经 player:profile 广播同步。
                this.opts.onAvatarChange?.(cur.id);
            } catch (err) {
                this.ui.showHint(`保存失败：${errorMessage(err)}`);
            } finally {
                syncHud();
                use.disabled = false;
            }
        });
        void show();
    }
}

/**
 * AvatarStage: a lightweight standalone PlayCanvas app that shows one avatar
 * GLB centered, with auto-playing looping animation, pointer-drag rotation
 * and wheel/pinch zoom (touch + mouse).
 */
class AvatarStage {
    private pc: typeof import('playcanvas') | null = null;
    private app: any = null;
    private camera: any = null;
    private holder: any = null;
    private observer: ResizeObserver | null = null;
    private destroyed = false;
    private target = { x: 0, y: 1, z: 0 };
    private distance = 3.2;
    private baseDistance = 3.2;
    private yaw = 0;
    private pitch = 0.16;
    private pointers = new Map<number, { x: number; y: number }>();
    private cache = new Map<string, any>();

    static async create(container: HTMLElement): Promise<AvatarStage> {
        const stage = new AvatarStage();
        const pc = await import('playcanvas');
        if (stage.destroyed || !container.isConnected) return stage;
        stage.pc = pc;
        const canvas = document.createElement('canvas');
        canvas.className = 'wa-canvas';
        canvas.style.touchAction = 'none';
        container.appendChild(canvas);
        const app = new pc.Application(canvas, { graphicsDeviceOptions: { alpha: true, antialias: true } });
        app.setCanvasFillMode(pc.FILLMODE_NONE, 1, 1);
        app.setCanvasResolution(pc.RESOLUTION_AUTO);
        app.scene.ambientLight = new pc.Color(0.72, 0.76, 0.82);
        const camera = new pc.Entity('StageCamera');
        camera.addComponent('camera', { clearColor: new pc.Color(0, 0, 0, 0), nearClip: 0.1, farClip: 120, fov: 40 });
        app.root.addChild(camera);
        const light = new pc.Entity('StageLight');
        light.addComponent('light', { type: 'directional', intensity: 1.8 });
        light.setEulerAngles(45, 25, 0);
        app.root.addChild(light);
        const fill = () => {
            if (!stage.destroyed && container.isConnected) {
                app.resizeCanvas(Math.max(1, container.clientWidth), Math.max(1, container.clientHeight));
            }
        };
        const observer = new ResizeObserver(fill);
        observer.observe(container);
        stage.app = app;
        stage.camera = camera;
        stage.observer = observer;
        fill();
        app.on('update', () => stage._orbit());
        app.start();
        stage._bindControls(canvas);
        return stage;
    }

    private _bindControls(canvas: HTMLCanvasElement): void {
        canvas.addEventListener('pointerdown', (e) => {
            canvas.setPointerCapture(e.pointerId);
            this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        });
        canvas.addEventListener('pointermove', (e) => {
            const prev = this.pointers.get(e.pointerId);
            if (!prev) return;
            const cur = { x: e.clientX, y: e.clientY };
            if (this.pointers.size === 1) {
                this.yaw -= (cur.x - prev.x) * 0.01;
                this.pitch = Math.max(-0.15, Math.min(1.1, this.pitch + (cur.y - prev.y) * 0.004));
            } else if (this.pointers.size === 2) {
                const ids = [...this.pointers.keys()];
                const other = this.pointers.get(ids[0] === e.pointerId ? ids[1] : ids[0]);
                if (other) {
                    const oldDist = Math.hypot(prev.x - other.x, prev.y - other.y);
                    const newDist = Math.hypot(cur.x - other.x, cur.y - other.y);
                    if (oldDist > 0) this._zoom(oldDist / newDist);
                }
            }
            this.pointers.set(e.pointerId, cur);
        });
        const release = (e: PointerEvent) => { this.pointers.delete(e.pointerId); };
        canvas.addEventListener('pointerup', release);
        canvas.addEventListener('pointercancel', release);
        canvas.addEventListener('wheel', (e) => {
            e.preventDefault();
            this._zoom(1 + e.deltaY * 0.0012);
        }, { passive: false });
    }

    private _zoom(factor: number): void {
        this.distance = Math.max(this.baseDistance * 0.35, Math.min(this.baseDistance * 2.6, this.distance * factor));
    }

    private _orbit(): void {
        if (!this.camera || !this.pc) return;
        const d = this.distance;
        const Vec3 = this.pc.Vec3;
        const t = this.target;
        this.camera.setPosition(t.x + Math.sin(this.yaw) * d, t.y + d * this.pitch, t.z + Math.cos(this.yaw) * d);
        this.camera.lookAt(new Vec3(t.x, t.y, t.z));
    }

    /** Load + display an avatar; auto-plays its looping idle animation. */
    async show(avatar: AvatarRow): Promise<void> {
        if (!this.app || !this.pc || this.destroyed) return;
        if (this.holder) { this.holder.destroy(); this.holder = null; }
        const cfg = avatar.config ?? {};
        const asset = await this._load(avatar.modelUrl);
        if (this.destroyed) return;
        const model = asset.resource.instantiateRenderEntity();
        const holder = new this.pc.Entity('AvatarHolder');
        holder.addChild(model);
        this.app.root.addChild(holder);
        const scale = Number(cfg.avatarScale) || 1;
        holder.setLocalScale(scale, scale, scale);
        holder.setLocalEulerAngles(0, Number(cfg.avatarRotationOffset) || 0, 0);
        const bounds = new this.pc.BoundingBox();
        let has = false;
        model.findComponents('render').forEach((r: any) => r.meshInstances.forEach((m: any) => {
            if (has) bounds.add(m.aabb);
            else { bounds.copy(m.aabb); has = true; }
        }));
        const t = has ? bounds.center.clone() : new this.pc.Vec3(0, 1, 0);
        this.target = { x: t.x, y: t.y, z: t.z };
        this.baseDistance = Math.max(2, has ? bounds.halfExtents.length() * 3.2 : 3);
        this.distance = this.baseDistance;
        this.yaw = 0;
        this.pitch = 0.16;
        const clips = asset.resource.animations || [];
        if (clips.length) {
            model.addComponent('anim', { activate: true });
            const layer = model.anim.baseLayer ?? model.anim.addLayer('Base');
            const states: [string, string][] = [];
            clips.forEach((clip: any, i: number) => {
                if (clip.resource) {
                    layer.assignAnimation(`clip${i}`, clip.resource, 1, true);
                    states.push([`clip${i}`, clip.resource.name || clip.name || `动画${i + 1}`]);
                }
            });
            const idle = states.find(([, n]) => n === (cfg.idleAnim ?? 'Idle')) ?? states[0];
            if (idle) layer.play(idle[0]);
        }
        this.holder = holder;
    }

    private async _load(url: string): Promise<any> {
        if (!this.cache.has(url)) {
            const asset = await new Promise<any>((resolve, reject) => {
                this.app.assets.loadFromUrlAndFilename(url, 'avatar.glb', 'container',
                    (err: unknown, a: any) => err ? reject(new Error('模型加载失败，请检查资源')) : resolve(a));
            });
            this.cache.set(url, asset);
        }
        return this.cache.get(url);
    }

    destroy(): void {
        this.destroyed = true;
        this.observer?.disconnect();
        this.observer = null;
        try { this.app?.destroy(); } catch { /* ignore */ }
        this.app = null;
        this.camera = null;
        this.holder = null;
        this.cache.clear();
    }
}

export { WorldUI };
